import { type ModalNames, useModalActions } from '@carboncopyco/components';
import { useClickOutside } from '@carboncopyco/utils';
import { useMemo, useState } from 'react';

export interface ModalConfig {
  noBackground?: boolean;
  removeCloseIcon?: boolean;
  containerClass?: string;
  locked?: boolean;
  isCenterPosition?: boolean;
  onClose?: () => void;
  closeOnlyFromCloseIcon?: boolean;
  title?: string;
}

export enum ModalType {
  APP_VERSION = 'app-version-modal',
  NEW_VERSION_DOWNLOADED = 'version-download-modal',
  SEND_LOGS = 'send-logs-modal',
  DIAGNOSTICS_CHECK = 'diagnostic-check-modal',
  RESTART_APP = 'restart-app-modal',
  APP_SETTINGS = 'app-settings-modal',
  GO_TO_ASSET = 'go-to-asset-modal',
}
export const useModals = () => {
  const [modalData, setModalData] = useState<any>(null);
  const [modalType, setModalType] = useState<ModalNames | null>(null);
  const [forceModal, setForceModal] = useState<boolean>(false);
  const { modalActionsByName } = useModalActions();
  const modalActions = modalType ? modalActionsByName(modalType) : undefined;

  const handleSetModalType = (modalName: ModalNames) => {
    setModalType(modalName);
    const { openModal } = modalActionsByName(modalName);
    openModal();
  };

  const closeModal = () => {
    modalActions?.closeModal();
    setModalType(null);
    setForceModal(false);
  };

  const modalConfigByType = useMemo((): ModalConfig => {
    switch (modalType) {
      case ModalType.DIAGNOSTICS_CHECK:
        return {
          locked: true,
          noBackground: false,
          removeCloseIcon: false,
          onClose: () => closeModal(),
          closeOnlyFromCloseIcon: true,
          containerClass: 'w-[812px] h-[456px]',
          isCenterPosition: true,
          title: 'Diagnostic Check',
        };
      case ModalType.RESTART_APP:
        return {
          locked: true,
          isCenterPosition: false,
        };
      case ModalType.APP_SETTINGS:
        return {
          locked: true,
          isCenterPosition: false,
        };
      case ModalType.GO_TO_ASSET:
        return {
          noBackground: true,
          removeCloseIcon: true,
          containerClass: 'w-74 bg-ds-danger p-0',
          locked: false,
          isCenterPosition: false,
        };
      default:
        return {
          noBackground: true,
          removeCloseIcon: true,
          containerClass: 'w-74 bg-ds-natural-50',
          locked: false,
          isCenterPosition: true,
        };
    }
  }, [modalType]);

  const modalRef = useClickOutside(() => {
    if (!forceModal && !modalConfigByType?.locked) closeModal();
  }, [forceModal, modalType]);

  return {
    modalType,
    forceModal,
    setForceModal,
    handleSetModalType,
    closeModal,
    modalRef,
    modalData,
    setModalData,
    setModalType,
    modalActions,
    modalConfigByType,
  };
};
